/**
 * Server-side reporting and CSV import contracts.
 *
 * These are not part of the terminal sync protocol (see sync.ts): reports are
 * read by the admin web backend only, over plain authenticated HTTP, and the
 * product importer is an admin-only maintenance tool. They live here anyway
 * because the rule is the same one as everywhere else in this monorepo: a
 * shape that crosses a wire is declared once, in @equinox/shared, and every
 * consumer imports it instead of redeclaring it.
 */
import { z } from 'zod';
import { cents, isoDateTime, nonNegativeCents, uuid } from './domain.js';
import { PaymentMethod } from './domain.js';
// ---------------------------------------------------------------------------
// GET /api/v1/reports
// ---------------------------------------------------------------------------
export const ReportQuery = z.object({
    from: isoDateTime,
    to: isoDateTime,
    method: PaymentMethod.optional(),
    staffId: uuid.optional(),
    terminalId: uuid.optional(),
});
export const PaymentMethodBreakdown = z.object({
    method: PaymentMethod,
    amountCents: nonNegativeCents,
    paymentCount: z.number().int().nonnegative(),
});
export const DiscountByReason = z.object({
    reasonId: uuid.nullable(),
    label: z.string(),
    amountCents: nonNegativeCents,
});
export const TopItem = z.object({
    productId: uuid.nullable(),
    sku: z.string(),
    name: z.string(),
    /** sale_lines.qty is numeric(10,3): fractional units (e.g. weighed goods) are legal. */
    unitsSold: z.number().nonnegative(),
    revenueCents: nonNegativeCents,
});
export const SalesReport = z.object({
    from: isoDateTime,
    to: isoDateTime,
    /** Sum of subtotal_cents, before discounts. Voided sales excluded. */
    grossSalesCents: nonNegativeCents,
    discountTotalCents: nonNegativeCents,
    /** grossSalesCents - discountTotalCents. */
    netSalesCents: nonNegativeCents,
    transactionCount: z.number().int().nonnegative(),
    /** Voided sales are never folded into the totals above; counted here only. */
    voidedCount: z.number().int().nonnegative(),
    itemsSold: z.number().nonnegative(),
    averageTicketCents: nonNegativeCents,
    /**
     * Cost of goods sold: SUM(qty × unit_cost_cents) over lines whose cost
     * was known when the sale landed (the server snapshots product cost onto
     * each line at ingestion). Admin-only — the terminal's LocalReport
     * deliberately has no cost or profit fields.
     */
    costOfGoodsCents: nonNegativeCents,
    /** netSalesCents − costOfGoodsCents. Negative is a legal, honest value. */
    netProfitCents: z.number().int(),
    /** Units sold whose line has no cost snapshot — the profit figure above
     * does not account for them; 0 means full coverage. */
    itemsWithoutCost: z.number().nonnegative(),
    discountByReason: z.array(DiscountByReason),
    byPaymentMethod: z.array(PaymentMethodBreakdown),
    topItemsByUnits: z.array(TopItem),
    topItemsByRevenue: z.array(TopItem),
});
// ---------------------------------------------------------------------------
// GET /api/v1/reports/shifts — admin only. The only place variance travels.
// ---------------------------------------------------------------------------
export const ShiftReportQuery = z.object({
    from: isoDateTime.optional(),
    to: isoDateTime.optional(),
    staffId: uuid.optional(),
    terminalId: uuid.optional(),
});
export const ShiftReportRow = z.object({
    shiftId: uuid,
    staffId: uuid,
    staffName: z.string(),
    terminalId: uuid,
    terminalName: z.string(),
    openedAt: isoDateTime,
    closedAt: isoDateTime.nullable(),
    openingBalanceCents: nonNegativeCents,
    closingBalanceCents: nonNegativeCents.nullable(),
    expectedCashCents: cents.nullable(),
    varianceCents: cents.nullable(),
    status: z.enum(['open', 'closed']),
});
export const ShiftReport = z.object({
    shifts: z.array(ShiftReportRow),
});
// ---------------------------------------------------------------------------
// POST /api/v1/import/products
// ---------------------------------------------------------------------------
export const ImportProductRow = z.object({
    sku: z.string().trim().min(1),
    name: z.string().trim().min(1),
    category: z.string().trim().min(1),
    /** Decimal string as it arrives in the CSV, e.g. "125.50". Never a number. */
    price: z.string().trim().min(1),
    imageUrl: z.string().trim().optional(),
    // stock & media: optional `stock` column (db/migrations/005_stock_media.sql).
    // A decimal string, same convention as `price` — parsed, not trusted as a
    // number, and absent entirely when the column isn't in the file at all
    // (see services/import.ts, which only sets this key when the header was
    // present, so "column omitted" and "cell left blank" stay distinguishable).
    stock: z.string().trim().optional(),
});
export const ImportRowError = z.object({
    /** 1-based, counting the header row as row 0 so it lines up with a spreadsheet. */
    row: z.number().int().positive(),
    sku: z.string().optional(),
    message: z.string(),
});
export const ImportResult = z.object({
    dryRun: z.boolean(),
    totalRows: z.number().int().nonnegative(),
    created: z.number().int().nonnegative(),
    updated: z.number().int().nonnegative(),
    failed: z.number().int().nonnegative(),
    errors: z.array(ImportRowError),
});
//# sourceMappingURL=reports.js.map